import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Spinner, Empty, StatusBadge, Steps, History } from "@/components/common";
import { useApi, errMsg, sar, fmtDate } from "@/lib/api";
import { ORDER_STATUS, ORDER_FLOW } from "@/lib/labels";

const OrderCard = ({ o, onCancel }) => (
  <div className="min-w-0 rounded-2xl border border-[#E5DEC9] bg-white p-6" data-testid={`order-card-${o.code}`}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <p className="font-bold text-[#0F382C] ltr-num" data-testid={`order-code-${o.code}`}>{o.code}</p>
        <p className="text-xs text-[#5C7067]">{fmtDate(o.created_at)}</p>
      </div>
      <StatusBadge map={ORDER_STATUS} status={o.status} testId={`order-status-${o.code}`} />
    </div>
    {o.status !== "cancelled" && <Steps flow={ORDER_FLOW} map={ORDER_STATUS} status={o.status} testId={`order-steps-${o.code}`} />}
    <ul className="mt-3 divide-y divide-[#F0EADB] text-sm">
      {o.items.map((it, i) => (
        <li key={i} className="flex justify-between py-2">
          <span>{it.name} × {it.qty} {it.with_installation && <span className="text-xs text-[#C59B27]">(مع التركيب)</span>}</span>
          <span>{sar(it.line_total)}</span>
        </li>
      ))}
    </ul>
    <div className="mt-3 space-y-1 border-t border-[#E5DEC9] pt-3 text-sm">
      <div className="flex justify-between text-[#5C7067]"><span>رسوم التركيب</span><span>{sar(o.installation_total)}</span></div>
      <div className="flex justify-between font-extrabold text-[#0F382C]"><span>الإجمالي</span><span data-testid={`order-total-${o.code}`}>{sar(o.total)}</span></div>
      <p className="flex items-center gap-1 text-xs text-[#5C7067]"><Banknote className="h-3.5 w-3.5" />الدفع عند التنفيذ</p>
    </div>
    {o.assignee_name && <p className="mt-2 text-sm text-[#5C7067]">الفني: {o.assignee_name}</p>}
    <div className="mt-3 flex items-center justify-between">
      <details><summary className="cursor-pointer text-sm font-semibold text-[#0F382C]">تتبع الطلب</summary><div className="mt-3"><History items={o.history} map={ORDER_STATUS} /></div></details>
      {["new", "confirmed"].includes(o.status) && (
        <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600" onClick={() => onCancel(o)} data-testid={`cancel-order-${o.code}`}>إلغاء</Button>
      )}
    </div>
  </div>
);

export default function OwnerOrders() {
  const api = useApi();
  const [orders, setOrders] = useState(null);
  const load = useCallback(() => {
    api("get", "/owner/orders").then(setOrders).catch((e) => toast.error(errMsg(e)));
  }, [api]);
  useEffect(() => {
    load();
  }, [load]);
  const cancel = async (o) => {
    if (!window.confirm("تأكيد إلغاء الطلب؟")) return;
    try {
      await api("post", `/owner/orders/${o.id}/cancel`);
      toast.success("تم إلغاء الطلب");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="owner-orders-page">
      <PageHeader title="طلباتي" subtitle="تتبع طلبات متجر الخدمات الشخصية" />
      {orders === null ? <Spinner /> : orders.length === 0 ? (
        <Empty icon={Package} title="لا توجد طلبات بعد" text="تصفح المتجر وأضف منتجات إلى السلة" testId="orders-empty" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">{orders.map((o) => <OrderCard key={o.id} o={o} onCancel={cancel} />)}</div>
      )}
    </div>
  );
}
